import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Wand2, Plus, Trash2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const clean = (s) => (s || "").replace(/[^a-zA-Z']/g, "").toLowerCase().slice(0, 40);

export default function AutocorrectDialog({ open, onOpenChange, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nf, setNf] = useState("");
  const [nt, setNt] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/autocorrect")
      .then(({ data }) => setRows(data.rules || []))
      .catch(() => toast.error("Couldn't load your list"))
      .finally(() => setLoading(false));
  }, [open]);

  const addRow = () => {
    const from = clean(nf);
    const to = nt.trim();
    if (!from) { toast.message("Type the word to replace (letters only)"); return; }
    if (!to) { toast.message("Type what it should become"); return; }
    if (rows.some((r) => r.from === from)) { toast.message(`You already have a rule for "${from}"`); return; }
    setRows((r) => [...r, { from, to }]);
    setNf(""); setNt("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/autocorrect", { rules: rows });
      setRows(data.rules || []);
      onSaved?.(data.rules || []);
      toast.success("Your autocorrect list is saved");
      onOpenChange(false);
    } catch { toast.error("Couldn't save your list"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-lg max-h-[88vh] overflow-y-auto" data-testid="autocorrect-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> My autocorrect</DialogTitle>
          <DialogDescription>
            Teach the editor your own fixes and shortcuts. Type a word and it's swapped as you write — e.g. <span className="font-mono-code">kdf → King's Defence Force</span>. Your rules sync across your devices and always win over the built-in ones.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <>
              <div className="hidden sm:grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold px-1">
                <span>When I type</span><span></span><span>Replace with</span><span></span>
              </div>
              <div className="space-y-2">
                {rows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2" data-testid={`ac-row-${row.from}`}>
                    <Input value={row.from} onChange={(e) => setRows((r) => r.map((x, i) => i === idx ? { ...x, from: clean(e.target.value) } : x))}
                      className="h-9 font-mono-code" placeholder="teh" data-testid={`ac-from-${idx}`} />
                    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Input value={row.to} onChange={(e) => setRows((r) => r.map((x, i) => i === idx ? { ...x, to: e.target.value } : x))}
                      className="h-9" placeholder="the" data-testid={`ac-to-${idx}`} />
                    <Button variant="ghost" size="icon" onClick={() => setRows((r) => r.filter((_, i) => i !== idx))}
                      data-testid={`ac-delete-${idx}`} className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {!rows.length && <p className="text-sm text-muted-foreground text-center py-3">No personal rules yet — add one below.</p>}
              </div>

              <div className="rounded-lg border border-border bg-secondary/20 p-3 space-y-2.5">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                  <Input value={nf} onChange={(e) => setNf(e.target.value)} placeholder="When I type (e.g. kdf)" className="h-9 font-mono-code" data-testid="ac-new-from"
                    onKeyDown={(e) => { if (e.key === "Enter") addRow(); }} />
                  <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  <Input value={nt} onChange={(e) => setNt(e.target.value)} placeholder="Replace with (e.g. King's Defence Force)" className="h-9" data-testid="ac-new-to"
                    onKeyDown={(e) => { if (e.key === "Enter") addRow(); }} />
                </div>
                <Button onClick={addRow} size="sm" data-testid="ac-add-btn" className="gap-1.5 w-full"><Plus className="w-4 h-4" /> Add rule</Button>
                <p className="text-[11px] text-muted-foreground">Triggers are letters only and case-insensitive. It replaces whole words as you type a space or punctuation.</p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving || loading} data-testid="ac-save-btn" className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Save list
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
